/**
 * Application error handling
 */
// Error codes that map to HTTP status codes
export var ErrorCode;
(function (ErrorCode) {
    ErrorCode["BAD_REQUEST"] = "BAD_REQUEST";
    ErrorCode["UNAUTHORIZED"] = "UNAUTHORIZED";
    ErrorCode["FORBIDDEN"] = "FORBIDDEN";
    ErrorCode["NOT_FOUND"] = "NOT_FOUND";
    ErrorCode["CONFLICT"] = "CONFLICT";
    ErrorCode["RATE_LIMITED"] = "RATE_LIMITED";
    ErrorCode["INTERNAL"] = "INTERNAL";
    ErrorCode["SERVICE_UNAVAILABLE"] = "SERVICE_UNAVAILABLE";
})(ErrorCode || (ErrorCode = {}));
// Map error codes to HTTP status codes
export const ERROR_STATUS_MAP = {
    [ErrorCode.BAD_REQUEST]: 400,
    [ErrorCode.UNAUTHORIZED]: 401,
    [ErrorCode.FORBIDDEN]: 403,
    [ErrorCode.NOT_FOUND]: 404,
    [ErrorCode.CONFLICT]: 409,
    [ErrorCode.RATE_LIMITED]: 429,
    [ErrorCode.INTERNAL]: 500,
    [ErrorCode.SERVICE_UNAVAILABLE]: 503,
};
// Map error codes to tRPC error codes
const ERROR_TRPC_MAP = {
    [ErrorCode.BAD_REQUEST]: 'BAD_REQUEST',
    [ErrorCode.UNAUTHORIZED]: 'UNAUTHORIZED',
    [ErrorCode.FORBIDDEN]: 'FORBIDDEN',
    [ErrorCode.NOT_FOUND]: 'NOT_FOUND',
    [ErrorCode.CONFLICT]: 'CONFLICT',
    [ErrorCode.RATE_LIMITED]: 'TOO_MANY_REQUESTS',
    [ErrorCode.INTERNAL]: 'INTERNAL_SERVER_ERROR',
    [ErrorCode.SERVICE_UNAVAILABLE]: 'INTERNAL_SERVER_ERROR',
};
/**
 * Application error class with structured error information
 */
export class AppError extends Error {
    code;
    statusCode;
    context;
    constructor(code, message, context) {
        super(message);
        this.name = 'AppError';
        this.code = code;
        this.statusCode = ERROR_STATUS_MAP[code];
        this.context = context;
    }
    toJSON() {
        return {
            code: this.code,
            message: this.message,
            ...(this.context && { context: this.context }),
        };
    }
}
/**
 * Convert AppError code to tRPC error code
 */
export function appErrorToTrpcCode(code) {
    return ERROR_TRPC_MAP[code];
}
/**
 * Stable, machine-readable reasons for errors a client must tell apart from
 * others with the same code. Put one in an AppError's `context.reason`; the
 * tRPC error formatter copies it to `error.data.reason`, so clients branch on
 * it instead of matching the (human, changeable) message text.
 */
export const ErrorReason = {
    /** A typed confirmation e-mail does not match the account being deleted. */
    CONFIRM_EMAIL_MISMATCH: 'CONFIRM_EMAIL_MISMATCH',
};
const ERROR_REASONS = new Set(Object.values(ErrorReason));
/** True for a value that is one of the {@link ErrorReason} codes. */
export function isErrorReason(value) {
    return typeof value === 'string' && ERROR_REASONS.has(value);
}
/**
 * The `context.reason` of an AppError, when it is one of the
 * {@link ErrorReason} codes. Anything else is dropped: `context` is free-form
 * and may carry internal detail, and only the enum is a contract with clients.
 */
export function appErrorReason(error) {
    const reason = error.context?.reason;
    return isErrorReason(reason) ? reason : undefined;
}
/**
 * Helper functions for creating common errors
 */
export const Errors = {
    badRequest: (message, context) => new AppError(ErrorCode.BAD_REQUEST, message, context),
    unauthorized: (message = 'Authentication required', context) => new AppError(ErrorCode.UNAUTHORIZED, message, context),
    forbidden: (message = 'Access denied') => new AppError(ErrorCode.FORBIDDEN, message),
    notFound: (resource, id) => new AppError(ErrorCode.NOT_FOUND, id ? `${resource} with id ${id} not found` : `${resource} not found`, { resource, id }),
    conflict: (message, context) => new AppError(ErrorCode.CONFLICT, message, context),
    rateLimited: (message = 'Too many requests') => new AppError(ErrorCode.RATE_LIMITED, message),
    internal: (message = 'An unexpected error occurred') => new AppError(ErrorCode.INTERNAL, message),
    serviceUnavailable: (service) => new AppError(ErrorCode.SERVICE_UNAVAILABLE, `${service} is currently unavailable`, { service }),
};
//# sourceMappingURL=errors.js.map